import { useMemo, useRef, useState } from 'react';
import {
  ArrowDownAZ,
  Download,
  Folder,
  MoreHorizontal,
  Plus,
  Search,
  Upload,
  X,
} from 'lucide-react';
import { cn } from '@/shared/cn';
import {
  HEADER_ICON_BUTTON,
  MENU_CONTENT,
  MENU_ITEM_BASE,
  SURFACE_PAGE_TAB_ACTIVE,
  SURFACE_PAGE_TAB_INACTIVE,
} from '@/shared/ui-class';
import { Commentable } from '@/shared/comments/Commentable';
import { useClickOutside } from '@/versions/v1/chrome/sidebar/AccountMenu';
import {
  SEED_ARTIFACTS,
  SEED_FILE_FOLDERS,
  SEED_FILES,
  type SeedArtifact,
  type SeedFile,
} from '@/versions/v1/shared/files-seed';
import { ArtifactViewerPanel } from './ArtifactViewerPanel';
import {
  filterFiles,
  groupFilesByFolder,
  sortFiles,
  type FileSortKey,
} from './FilesPane.transform';

type FilesTab = 'files' | 'artifacts';

export function FilesPane({ onClose }: { onClose: () => void }) {
  const [tab, setTab] = useState<FilesTab>('files');
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState<FileSortKey>('name');
  const [folderFilter, setFolderFilter] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [viewingArtifact, setViewingArtifact] = useState<SeedArtifact | null>(null);
  const uploadRef = useRef<HTMLDivElement | null>(null);

  useClickOutside(uploadRef, () => setUploadOpen(false), uploadOpen);

  const files = useMemo(() => {
    const filtered = filterFiles(SEED_FILES, query, folderFilter);
    return sortFiles(filtered, sortKey);
  }, [query, folderFilter, sortKey]);

  const grouped = useMemo(() => groupFilesByFolder(files), [files]);

  if (viewingArtifact) {
    return (
      <Commentable name="Files">
        <aside className="flex h-full w-full flex-col border-l border-zinc-200 bg-[var(--background)] dark:border-zinc-800">
          <ArtifactViewerPanel
            artifact={viewingArtifact}
            onBack={() => setViewingArtifact(null)}
            onClose={onClose}
          />
        </aside>
      </Commentable>
    );
  }

  return (
    <Commentable name="Files">
      <aside className="flex h-full w-full flex-col border-l border-zinc-200 bg-[var(--background)] dark:border-zinc-800">
        <header className="flex h-12 flex-shrink-0 items-center justify-between gap-2 border-b border-divider px-3">
          <div className="flex items-center gap-1 rounded-full bg-[var(--glass-surface-soft)] p-0.5">
            <button
              type="button"
              onClick={() => setTab('files')}
              className={cn(tab === 'files' ? SURFACE_PAGE_TAB_ACTIVE : SURFACE_PAGE_TAB_INACTIVE)}
            >
              Files
            </button>
            <button
              type="button"
              onClick={() => setTab('artifacts')}
              className={cn(
                tab === 'artifacts' ? SURFACE_PAGE_TAB_ACTIVE : SURFACE_PAGE_TAB_INACTIVE,
              )}
            >
              Artifacts
            </button>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close files pane"
            title="Close"
            className={HEADER_ICON_BUTTON}
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {tab === 'files' ? (
          <>
            <div className="space-y-2 border-b border-divider px-3 py-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search files"
                  className="w-full rounded-md border border-[var(--glass-border)] bg-transparent py-1.5 pl-8 pr-2 text-xs"
                />
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={folderFilter ?? ''}
                  onChange={(event) =>
                    setFolderFilter(event.target.value ? event.target.value : null)
                  }
                  className="min-w-0 flex-1 rounded-md border border-[var(--glass-border)] bg-transparent px-2 py-1 text-xs"
                >
                  <option value="">All folders</option>
                  {SEED_FILE_FOLDERS.map((folder) => (
                    <option key={folder} value={folder}>
                      {folder}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() =>
                    setSortKey((prev) => (prev === 'name' ? 'updated' : 'name'))
                  }
                  className="inline-flex items-center gap-1 rounded-md border border-[var(--glass-border)] px-2 py-1 text-xs"
                  title="Toggle sort"
                >
                  <ArrowDownAZ className="h-3.5 w-3.5" />
                  {sortKey === 'name' ? 'Name' : 'Updated'}
                </button>
                <div className="relative" ref={uploadRef}>
                  <button
                    type="button"
                    onClick={() => setUploadOpen((value) => !value)}
                    className="inline-flex items-center gap-1 rounded-md border border-[var(--glass-border)] px-2 py-1 text-xs"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Upload
                  </button>
                  {uploadOpen ? (
                    <div className={cn(MENU_CONTENT, 'absolute right-0 top-full z-50 mt-1 min-w-[160px] p-1')}>
                      <button type="button" className={MENU_ITEM_BASE}>
                        <Upload className="h-4 w-4" />
                        Upload files
                      </button>
                      <button type="button" className={MENU_ITEM_BASE}>
                        <Folder className="h-4 w-4" />
                        Upload folder
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="scrollbar-subtle min-h-0 flex-1 overflow-y-auto px-2 py-2">
              {Object.entries(grouped).map(([folder, rows]) => (
                <section key={folder} className="mb-4">
                  <h3 className="mb-1 px-2 text-xs font-medium uppercase tracking-wide text-muted">
                    {folder}
                  </h3>
                  {rows.map((file) => (
                    <FileRow key={file.id} file={file} />
                  ))}
                </section>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between border-b border-divider px-3 py-2">
              <span className="text-xs text-muted">{SEED_ARTIFACTS.length} artifacts</span>
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-zinc-600 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-900"
              >
                <Download className="h-3.5 w-3.5" />
                Download all
              </button>
            </div>
            <div className="scrollbar-subtle min-h-0 flex-1 overflow-y-auto px-2 py-2">
              {SEED_ARTIFACTS.map((artifact) => (
                <div
                  key={artifact.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setViewingArtifact(artifact)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setViewingArtifact(artifact);
                    }
                  }}
                  className="flex w-full cursor-pointer items-center gap-3 rounded-md px-2 py-2.5 text-left text-sm hover:bg-zinc-100 dark:hover:bg-zinc-900"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-zinc-900 dark:text-zinc-100">{artifact.name}</div>
                    <div className="text-xs text-muted">{artifact.meta}</div>
                  </div>
                  <div
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    <RowMenu label={artifact.name} />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </aside>
    </Commentable>
  );
}

function FileRow({ file }: { file: SeedFile }) {
  return (
    <div className="flex items-center gap-3 rounded-md px-2 py-2.5 text-sm hover:bg-zinc-100 dark:hover:bg-zinc-900">
      <file.Icon className="h-4 w-4 flex-shrink-0 text-zinc-400" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-zinc-900 dark:text-zinc-100">{file.name}</div>
        <div className="text-xs text-muted">{file.meta}</div>
      </div>
      <RowMenu label={file.name} />
    </div>
  );
}

function RowMenu({ label }: { label: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useClickOutside(ref, () => setOpen(false), open);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-label={`Actions for ${label}`}
        onClick={() => setOpen((value) => !value)}
        className="rounded-md p-1 text-zinc-400 hover:bg-zinc-200 hover:text-zinc-700 dark:hover:bg-zinc-800"
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {open ? (
        <div className={cn(MENU_CONTENT, 'absolute right-0 top-full z-50 mt-1 min-w-[140px] p-1')}>
          <button type="button" className={MENU_ITEM_BASE} onClick={() => setOpen(false)}>
            Open
          </button>
          <button type="button" className={MENU_ITEM_BASE} onClick={() => setOpen(false)}>
            Download
          </button>
          <button type="button" className={MENU_ITEM_BASE} onClick={() => setOpen(false)}>
            Remove
          </button>
        </div>
      ) : null}
    </div>
  );
}
